import { Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { KHONG_CO, soVN } from '../../../lib/dinh-dang';
import type { PhamViCong } from '../../../lib/pham-vi-cong';
import { SoHeSo, SoHeSoCache } from '../../ai-model/components/SoHeSo';
import { useTokenTheoModel } from '../hooks/useGoiNapToken';
import type { AiTokenModelUsageLine } from '../types';

const TH = 'whitespace-nowrap px-2 py-1 text-right font-normal first:pl-0 last:pr-0';
const TD = 'whitespace-nowrap px-2 py-1 text-right first:pl-0 last:pr-0';

/**
 * "Theo model" trong "Cách tính hạn mức" (ADR-0123 mục 3): token Fox đầu vào, đầu ra và số lượt của từng model trong
 * tháng. Chỉ tải khi khối được mở (`batDau`).
 *
 * `phamVi` là cổng đang xem, không phải việc API có trả trường hay không: ở cổng tenant chỉ có token Fox (khách không
 * thấy hệ số, token thật, cache); ở cổng nền tảng (chi tiết tenant) thêm token thật vào, đọc cache, ra và ba hệ số.
 * Nhân sự nền tảng xem chéo cổng tenant cũng chỉ thấy đúng những gì khách thấy.
 */
export function TokenTheoModel({
  tenantId,
  batDau,
  phamVi,
}: {
  tenantId: string;
  batDau: boolean;
  phamVi: PhamViCong;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const q = useTokenTheoModel(tenantId, batDau);
  const nenTang = phamVi === 'nen-tang';

  const so = (v: number | undefined) => (v === undefined ? KHONG_CO : soVN(v, locale));
  const tenDong = (d: AiTokenModelUsageLine) => d.label ?? d.model_key ?? t('banToken.theoModel.khongModel');

  return (
    <section className="space-y-1.5 border-t border-default pt-2" aria-labelledby={`theo-model-${tenantId}`}>
      <h4 id={`theo-model-${tenantId}`} className="text-xs font-semibold text-content-secondary">
        {t('banToken.theoModel.tieuDe')}
      </h4>
      {q.isLoading ? (
        <div className="space-y-1.5" aria-busy>
          {/* Khối này nằm trên nền `bg-subtle`, trùng màu mặc định của Skeleton. */}
          <Skeleton className="h-4 w-full bg-surface" />
          <Skeleton className="h-4 w-2/3 bg-surface" />
        </div>
      ) : q.error ? (
        <p role="alert" className="text-xs text-danger-700">
          {t('banToken.theoModel.loi', { loi: dichLoi(q.error) })}
        </p>
      ) : !q.data || q.data.items.length === 0 ? (
        <p className="text-xs text-content-muted">{t('banToken.theoModel.rong')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs tabular-nums">
            <caption className="sr-only">
              {t(nenTang ? 'banToken.theoModel.captionNenTang' : 'banToken.theoModel.caption')}
            </caption>
            <thead className="text-content-muted">
              {nenTang && (
                <tr>
                  <td className="py-1" />
                  <th scope="colgroup" colSpan={3} className={TH}>
                    {t('banToken.theoModel.nhomFox')}
                  </th>
                  <th scope="colgroup" colSpan={3} className={`${TH} border-l border-default`}>
                    {t('banToken.theoModel.nhomThat')}
                  </th>
                  <th scope="colgroup" colSpan={3} className={`${TH} border-l border-default`}>
                    {t('banToken.theoModel.nhomHeSo')}
                  </th>
                </tr>
              )}
              <tr>
                <th scope="col" className="py-1 pr-2 text-left font-normal">
                  {t('banToken.theoModel.cotModel')}
                </th>
                <th scope="col" className={TH}>
                  {t('banToken.theoModel.cotVao')}
                </th>
                <th scope="col" className={TH}>
                  {t('banToken.theoModel.cotRa')}
                </th>
                <th scope="col" className={TH}>
                  {t('banToken.theoModel.cotLuot')}
                </th>
                {nenTang &&
                  (['nhomThat', 'nhomHeSo'] as const).flatMap((nhom) => [
                    <th key={`${nhom}-vao`} scope="col" className={`${TH} border-l border-default`}>
                      {t('banToken.theoModel.cotVao')}
                    </th>,
                    <th key={`${nhom}-cache`} scope="col" className={TH}>
                      {t('banToken.theoModel.cotCache')}
                    </th>,
                    <th key={`${nhom}-ra`} scope="col" className={TH}>
                      {t('banToken.theoModel.cotRa')}
                    </th>,
                  ])}
              </tr>
            </thead>
            <tbody className="text-content-secondary">
              {q.data.items.map((d) => (
                <tr key={d.model_key ?? '-'} className="border-t border-default">
                  <th
                    scope="row"
                    className="max-w-[12rem] truncate py-1 pr-2 text-left font-normal text-content-primary"
                    title={tenDong(d)}
                  >
                    {tenDong(d)}
                  </th>
                  <td className={TD}>{soVN(d.fox_input, locale)}</td>
                  <td className={TD}>{soVN(d.fox_output, locale)}</td>
                  <td className={TD}>{soVN(d.calls, locale)}</td>
                  {nenTang && (
                    <>
                      <td className={`${TD} border-l border-default`}>{so(d.raw_input)}</td>
                      <td className={TD}>{so(d.raw_cache_read)}</td>
                      <td className={TD}>{so(d.raw_output)}</td>
                      <td className={`${TD} border-l border-default`}>
                        <SoHeSo giaTri={d.he_so_dau_vao} />
                      </td>
                      <td className={TD}>
                        <SoHeSoCache cache={d.he_so_cache} dauVao={d.he_so_dau_vao} />
                      </td>
                      <td className={TD}>
                        <SoHeSo giaTri={d.he_so_dau_ra} />
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-default font-medium text-content-primary">
                <th scope="row" className="py-1 pr-2 text-left font-medium">
                  {t('banToken.theoModel.tong')}
                </th>
                <td className={TD}>{soVN(q.data.fox_input, locale)}</td>
                <td className={TD}>{soVN(q.data.fox_output, locale)}</td>
                <td className={TD}>{soVN(q.data.items.reduce((s, d) => s + d.calls, 0), locale)}</td>
                {nenTang && <td colSpan={6} className="border-l border-default" />}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
